import {
  Button,
  Card,
  createColumnHelper,
  EmptyState,
  Icon,
  Input,
  Link,
  ProgressSpinner,
  resolveTablePageSizeChanged,
  SortingState,
  Table,
  TablePaginationControls,
} from '@dittolive/anvil'
import { ArrowUpRightIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { CollectionWithCount } from './index'

const TABLE_HEADER_ROW_HEIGHT = 48
const TABLE_ROW_HEIGHT = 64
const TABLE_FOOTER_HEIGHT = 40
const TABLE_MAX_HEIGHT = TABLE_HEADER_ROW_HEIGHT + TABLE_ROW_HEIGHT * 8
const TABLE_EMPTY_HEIGHT = 175

const columnHelper = createColumnHelper<CollectionWithCount>()

export interface CollectionsTableConfig {
  paths: {
    dqlEditor: (params: { appId: string; dql?: string }) => string
  }
  terminology: {
    databaseText: string
  }
  /** Extra classes for the wrapping Card, e.g. `shadow-none` when nested in another card. */
  className?: string
}

type Props = {
  appId: string
  systemCollectionsOnly?: boolean
  collections: CollectionWithCount[]
  isLoading: boolean
  isSuperAdmin?: boolean
  config: CollectionsTableConfig
}

export default function CollectionsTable({
  appId,
  systemCollectionsOnly,
  collections,
  isLoading,
  isSuperAdmin,
  config,
}: Props) {
  const { databaseText } = config.terminology

  const navigate = useNavigate()

  const [filter, setFilter] = useState('')

  // NOTE: I've separated this to a separate memoized variable in order to properly
  // determine whether to display the pagination controls.
  const filteredCollections = useMemo(
    () =>
      collections.filter((collection) =>
        collection.name.toLowerCase().includes(filter),
      ),
    [collections, filter],
  )

  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 })

  const { pageIndex, pageSize } = pagination
  const paginationMessage = useMemo(() => {
    const total = filteredCollections.length
    const from = pageIndex * pageSize + 1
    const to = Math.min(pageIndex * pageSize + pageSize, total)
    return to > 0 ? `${from}-${to} of ${total} items` : 'No items to display'
  }, [filteredCollections.length, pageIndex, pageSize])

  const pageCount = useMemo(
    () => Math.ceil(filteredCollections.length / pageSize),
    [filteredCollections.length, pageSize],
  )

  const allowDqlEditor = !systemCollectionsOnly || !!isSuperAdmin

  const getCollectionUrl = useCallback(
    (collection: CollectionWithCount) =>
      // Note: wrap the collection in quotes in case there are special characters, e.g. a hyphen,
      // which would otherwise cause the DQL query to fail.
      config.paths.dqlEditor({
        appId,
        dql: `SELECT * FROM \`${collection.name}\` LIMIT 100`,
      }),
    [appId, config.paths],
  )

  const renderCollectionLink = useCallback(
    (collection: CollectionWithCount) => {
      return (
        <Link
          data-testid="collectionLink"
          to={getCollectionUrl(collection)}
          state={{
            previousUrl: `${location.pathname}${location.search ?? ''}`,
          }}
          className="focus-visible:outline-focus-outline inline-flex items-center text-base outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
        >
          View
          <Icon
            className="text-foreground-subtle ml-1 size-4"
            svg={<ArrowUpRightIcon />}
          />
        </Link>
      )
    },
    [getCollectionUrl],
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => <span className="pl-[11.5px]">Collection</span>,
        cell: ({ row: { original: collection } }) => {
          return (
            <div className="flex w-full items-center justify-between">
              <div className="flex shrink-0 flex-col gap-0.5">
                <span className="text-foreground-normal">
                  {collection.name}
                </span>
                <span className="text-foreground-subtle">
                  {collection.count} document
                  {collection.count === 1 ? '' : 's'}
                </span>
              </div>

              {renderCollectionLink(collection)}
            </div>
          )
        },
        enableResizing: false,
      }),
    ],
    [renderCollectionLink],
  )

  const emptyStateRenderer = () => {
    // The Table renders the empty state inside an `h-0 p-0` cell, so the
    // wrapper provides the height that centers the content in the body area
    // (total empty height minus the sticky header row).
    const bodyHeight = TABLE_EMPTY_HEIGHT - TABLE_HEADER_ROW_HEIGHT
    return (
      <div
        className="flex items-center justify-center"
        style={{ height: bodyHeight }}
      >
        <EmptyState
          message={
            !!filter.length
              ? 'No collections match your search'
              : `This ${databaseText} has no collections yet`
          }
        />
      </div>
    )
  }

  const getTableHeight = (rowCount: number) => {
    if (rowCount > 0) {
      return pageCount > 1
        ? TABLE_MAX_HEIGHT
        : Math.min(
            TABLE_HEADER_ROW_HEIGHT + TABLE_ROW_HEIGHT * rowCount,
            TABLE_MAX_HEIGHT,
          )
    } else {
      return TABLE_EMPTY_HEIGHT
    }
  }

  return (
    <Card className={config.className}>
      <Card.Header
        className="flex h-12 items-center gap-x-2 overflow-hidden px-2"
        isFlushed
      >
        <Input
          className="w-full"
          leadingIcon={
            <Icon
              className="text-foreground-subtle size-4"
              svg={<MagnifyingGlassIcon />}
            />
          }
          value={filter}
          onChange={(e) => setFilter(e.target.value || '')}
          placeholder="Search collections"
          data-testid="collectionsTableFilter"
          disabled={collections.length === 0}
          title={
            collections.length === 0
              ? `This ${databaseText} has no collections yet`
              : 'Filter collections'
          }
        />

        {allowDqlEditor && (
          <Button
            asChild
            variant="primary"
            data-testid="dqlEditorLink"
            title="Open the DQL editor"
          >
            <Link
              to={config.paths.dqlEditor({
                appId,
                // Set a default query if there are collections, and the collection name exists
                ...(filteredCollections.length > 0 && {
                  dql: filteredCollections[0]?.name
                    ? `SELECT * FROM \`${filteredCollections[0].name}\` LIMIT 100`
                    : undefined,
                }),
              })}
              state={{
                previousUrl: `${location.pathname}${location.search ?? ''}`,
              }}
            >
              DQL editor
              <Icon svg={<ArrowUpRightIcon />} className="size-4" />
            </Link>
          </Button>
        )}
      </Card.Header>

      <Card.Body isFlushed>
        {isLoading && (
          <div className="border-border-normal min-h-120 flex items-center justify-center border-t py-8">
            <ProgressSpinner className="size-6" />
          </div>
        )}

        {!isLoading && (
          <Table
            data={filteredCollections}
            columns={columns}
            height={getTableHeight}
            emptyStateRenderer={emptyStateRenderer}
            tableCellClassName="first:pl-4 last:pr-4"
            tableRowClassName="h-16 cursor-pointer"
            onSelectActiveRow={(collection) => {
              if (!collection) return
              navigate(getCollectionUrl(collection), {
                state: {
                  previousUrl: `${location.pathname}${location.search ?? ''}`,
                },
              })
            }}
            options={{
              state: { pagination, sorting },
              onSortingChange: setSorting,
            }}
          />
        )}

        {!!collections.length && (
          <TablePaginationControls
            height={TABLE_FOOTER_HEIGHT}
            currentPage={pageIndex + 1}
            pageCount={pageCount}
            centerContent={paginationMessage}
            onSetPage={(page) =>
              setPagination((prev) => ({ ...prev, pageIndex: page - 1 }))
            }
            pageSize={pageSize}
            onSetPageSize={(size) =>
              setPagination((prev) => {
                const newPaginationState = {
                  ...prev,
                  pageSize: size,
                }
                const pageIndex = resolveTablePageSizeChanged(
                  prev,
                  newPaginationState,
                  collections.length,
                )
                return {
                  ...newPaginationState,
                  pageIndex,
                }
              })
            }
          />
        )}
      </Card.Body>
    </Card>
  )
}
